import { Modal, Table } from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useFetch, useNotificationContext } from "@4uhub/lib4uhub";
import { IInvAccoPayLoweringsLogData } from "../../../../models/inv-acco-pay-lowerings-log";
import { restartInvAccoPayLoweringsRetries } from "../../../../services/invAccoPayLowerings.service";
import MainApi from "../../../../services/mainApi.service";
import InvAccoPayLoweringsDetailsModal from "./InvAccoPayLoweringsDetailsModal";
import MacroFilter, { IFilterParams } from "./MacroFilter";

const INV_ACCO_PAY_LOWERINGS_ROUTE =
  process.env.REACT_APP_EXTERNAL +
  "/api/v1/ForubusInvAccoPayLowerings/ForubusInvAccoPayLowerings";

const invAccoPayLoweringsLogService = new MainApi<IInvAccoPayLoweringsLogData>(
  INV_ACCO_PAY_LOWERINGS_ROUTE,
);

const translationPath = "page.4uBus.logs.invoiceLoweringTotvs.";

const InvAccoPayLowerings = () => {
  const [refresh, setRefresh] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<IInvAccoPayLoweringsLogData | null>(null);

  const { sendRequest: restartInvAccoPayLoweringsRetriesReq } = useFetch(
    restartInvAccoPayLoweringsRetries,
  );

  const { setMessage } = useNotificationContext();

  const today = useMemo(() => new Date(), []);

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    Identifier: "",
    InvoiceIdentifier: "",
    AccountPayableIdentifier: "",
    StatusItem: "",
    CreatedStart: today,
    CreatedEnd: today,
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const columns: GridColDef<IInvAccoPayLoweringsLogData>[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "identifier") || "Identificador",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "invoiceIdentifier",
      headerName:
        t(translationPath + "invoice_identifier") ||
        "Identificador da Nota Fiscal",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "accountPayableIdentifier",
      headerName:
        t(translationPath + "account_payable_identifier") ||
        "Identificador Conta a Pagar",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "invoiceMaturitiesDueDate",
      headerName: t(translationPath + "due_date") || "Data de Vencimento",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";
        const date = new Date(params.value);
        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "accountPayableLoweringIdentifier",
      headerName: t(translationPath + "lowering_identifier") || "ID da Baixa",
      flex: 0.5,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "statusItem",
      headerName: t(translationPath + "status_item") || "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "-";

        const status = params.value.toUpperCase();

        const getStatusConfig = (status: string) => {
          const configs: Record<
            string,
            {
              color: "warning" | "info" | "success" | "error";
              key: string;
            }
          > = {
            P: { color: "warning", key: "pending" },
            PENDENTE: { color: "warning", key: "pending" },
            PR: { color: "info", key: "processing" },
            PROCESSANDO: { color: "info", key: "processing" },
            F: { color: "success", key: "finished" },
            FINALIZADO: { color: "success", key: "finished" },
            E: { color: "error", key: "error" },
            ERRO: { color: "error", key: "error" },
          };

          return (
            configs[status] || {
              color: "warning" as const,
              key: "pending",
            }
          );
        };

        const config = getStatusConfig(status);

        return (
          <Chip
            label={t(translationPath + `status.${config.key}`)}
            color={config.color}
            size="small"
            sx={{ color: "#FFFFFF" }}
          />
        );
      },
    },

    {
      field: "accountPayableLoweringOriginIdentifier",
      headerName: t(translationPath + "lowering_type") || "Tipo de Baixa",
      flex: 0.7,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const isReversal = params.value !== null && params.value !== undefined;
        return (
          <Chip
            label={
              isReversal
                ? t(translationPath + "reversal") || "Estorno"
                : t(translationPath + "normal") || "Normal"
            }
            color={isReversal ? "warning" : "success"}
            size="small"
            sx={{ color: "#FFFFFF" }}
          />
        );
      },
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const date = new Date(params.value);
        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "integratedDate",
      headerName:
        t(translationPath + "integrated_date") || "Data de Integração",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const dateStr = params.value.toString();
        if (
          dateStr.startsWith("0001-01-01") ||
          dateStr === "0001-01-01T00:00:00"
        ) {
          return "-";
        }

        const date = new Date(params.value);
        if (isNaN(date.getTime()) || date.getFullYear() === 1) {
          return "-";
        }

        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "retries",
      headerName: t(translationPath + "retries") || "Tentativas",
      flex: 0.5,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? 0;
      },
    },
    {
      field: "lastModified",
      headerName: t(translationPath + "last_modified") || "Última Modificação",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const date = new Date(params.value);
        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
  ];

  const onRefreshHandler = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleRestartInvAccoPayLoweringsRetries = useCallback(
    async (identifier: number) => {
      const { success } = await restartInvAccoPayLoweringsRetriesReq({
        identifier,
      });

      if (success) {
        onRefreshHandler();
        setMessage({
          message: t(`${translationPath}resetedSuccess`),
          type: "success",
        });
      }
    },
    [onRefreshHandler, restartInvAccoPayLoweringsRetriesReq, setMessage, t],
  );

  return (
    <>
      <Modal open={modalOpen} onClose={handleCloseModal} maxDialogWidth="lg">
        {selectedRow && (
          <InvAccoPayLoweringsDetailsModal
            loweringData={selectedRow}
            onClose={handleCloseModal}
          />
        )}
      </Modal>
      <Table<IInvAccoPayLoweringsLogData>
        title={t("components.menu.4ubus.logs.invoiceLoweringTotvs")}
        columns={columns}
        service={invAccoPayLoweringsLogService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Search..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        getRowId={(row) => row.identifier}
        rowActions={(params) => [
          {
            action: () => {
              setSelectedRow(params.row);
              setModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details") || "Ver detalhes",
          },
          params.row.statusItem === "ERRO"
            ? {
                action: () => {
                  handleRestartInvAccoPayLoweringsRetries(
                    params.row.identifier,
                  );
                },
                icon: <ReplayIcon fontSize="small" />,
                text: t(translationPath + "resync") || "Forçar reenvio",
              }
            : null,
        ]}
        serviceProps={useMemo(
          () => ({
            refresh,
            ...filterParams,
            CreatedStart: filterParams?.CreatedStart
              ? new Date(filterParams.CreatedStart.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
            CreatedEnd: filterParams?.CreatedEnd
              ? new Date(filterParams.CreatedEnd.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
          }),
          [refresh, filterParams],
        )}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default InvAccoPayLowerings;
